import { z } from 'zod';
import { difficultySchema } from '../enums.js';

export const dashboardBadgeSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  earned: z.boolean(),
  awardedAt: z.string().nullable(),
});
export type DashboardBadge = z.infer<typeof dashboardBadgeSchema>;

export const weakTopicSchema = z.object({
  tag: z.string(),
  attempts: z.number().int(),
  correct: z.number().int(),
  accuracy: z.number(),
});
export type WeakTopic = z.infer<typeof weakTopicSchema>;

export const recentAttemptSchema = z.object({
  exerciseTitle: z.string(),
  lessonSlug: z.string().nullable(),
  correct: z.boolean(),
  difficulty: difficultySchema,
  at: z.string(),
});
export type RecentAttempt = z.infer<typeof recentAttemptSchema>;

export const activityDaySchema = z.object({
  date: z.string(),
  exercisesSolved: z.number().int(),
  xpEarned: z.number().int(),
});
export type ActivityDay = z.infer<typeof activityDaySchema>;

export const dashboardSchema = z.object({
  displayName: z.string(),

  level: z.object({
    level: z.number().int(),
    title: z.string(),
    totalXp: z.number().int(),
    xpIntoLevel: z.number().int(),
    xpForNextLevel: z.number().int().nullable(),
    ratio: z.number(),
  }),

  progress: z.object({
    lessonsCompleted: z.number().int(),
    lessonsTotal: z.number().int(),
    exercisesSolved: z.number().int(),
    exercisesTotal: z.number().int(),
    /** Fraction of the whole course completed, 0–1. */
    courseRatio: z.number(),
  }),

  streak: z.object({
    current: z.number().int(),
    longest: z.number().int(),
    activeToday: z.boolean(),
  }),

  accuracy: z.object({
    correct: z.number().int(),
    attempts: z.number().int(),
    /** Percentage, 0–100. Null until there is at least one attempt. */
    percent: z.number().nullable(),
  }),

  weakTopics: z.array(weakTopicSchema),
  recentActivity: z.array(recentAttemptSchema),
  /** Last 30 local days, for the activity strip. */
  activityDays: z.array(activityDaySchema),
  badges: z.array(dashboardBadgeSchema),

  /** The single thing to do next. Null once the course is finished. */
  nextLesson: z
    .object({
      slug: z.string(),
      title: z.string(),
      moduleTitle: z.string(),
      level: z.number().int(),
    })
    .nullable(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;
